import { memo } from 'react';
import { Box, IconButton } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import FlipIcon from '@mui/icons-material/Flip';

import { StyledTooltip } from '@/modules/common/components/StyledTooltip';

import { ArrangeTransform } from './helpers';
import { BUTTON_GROUP_SX, BUTTON_SX } from './layout';

/**
 * `iconTurn`: the icon's own turn in degrees, null for icons that don't depend on direction.
 * The flip icon mirrors left/right; turned a quarter it mirrors top/bottom.
 */
const TRANSFORM_BUTTONS: {
  transform: ArrangeTransform;
  labelKey: string;
  Icon: SvgIconComponent;
  iconTurn: number | null;
}[] = [
  { transform: 'rotate90', labelKey: 'rotate_90', Icon: Rotate90DegreesCwIcon, iconTurn: null },
  { transform: 'flipHorizontal', labelKey: 'flip_horizontal', Icon: FlipIcon, iconTurn: 90 },
  { transform: 'flipVertical', labelKey: 'flip_vertical', Icon: FlipIcon, iconTurn: 0 },
];

type Props = {
  canRotate: boolean;
  canFlip: boolean;
  /** Shown instead of the flip buttons' labels when flipping isn't possible (e.g. templates) */
  flipDisabledReasonKey?: string;
  onTransform: (transform: ArrangeTransform) => void;
};

/** @description The rotate 90° and flip buttons of the dimensions panel */
function TransformButtonsComponent({
  canRotate,
  canFlip,
  flipDisabledReasonKey,
  onTransform,
}: Props) {
  return (
    <Box sx={BUTTON_GROUP_SX}>
      {TRANSFORM_BUTTONS.map(({ transform, labelKey, Icon, iconTurn }) => {
        const isRotate = transform === 'rotate90';
        const disabledReasonKey = isRotate ? undefined : flipDisabledReasonKey;
        const titleKey = disabledReasonKey ?? `interface:properties.dimensions.${labelKey}`;
        const disabled = isRotate ? !canRotate : !canFlip || disabledReasonKey !== undefined;

        return (
          <StyledTooltip key={transform} titleKey={titleKey}>
            <Box component='span' sx={{ display: 'flex', flex: 1 }}>
              <IconButton disabled={disabled} onClick={() => onTransform(transform)} sx={BUTTON_SX}>
                <Icon
                  sx={iconTurn === null ? undefined : { transform: `rotate(${iconTurn}deg)` }}
                />
              </IconButton>
            </Box>
          </StyledTooltip>
        );
      })}
    </Box>
  );
}

export const TransformButtons = memo(TransformButtonsComponent);
